import React, { useEffect, useRef, useState } from "react";
import { useQuill } from "react-quilljs";

import "quill/dist/quill.snow.css";
import "./style/additionalData.scoped.scss";

const TextInput = ({ value = "", onChange, readOnly, onBlur, children, ...rest }) => {
    const options = {
        theme: "snow",
        modules: {
            toolbar: [
                ["bold", "italic"],
                [{ script: "sub" }, { script: "super" }],
                [{ list: "ordered" }, { list: "bullet" }],
            ],
            clipboard: {
                matchers: [],
                matchVisual: false,
            },
            keyboard: {
                bindings: {},
            },
        },
        formats: ["bold", "italic", "script", "list"],
        placeholder: "",
        readOnly,
    };

    if (readOnly) {
        options.modules.toolbar = null;
    }

    const { quill, quillRef } = useQuill(options);
    const [loaded, setLoaded] = useState(false);
    const lastValueRef = useRef(null);

    function sanitizeOutput(html) {
        return html.replace(/<span.*?>.*?<\/span>/g, "");
    }

    // Use the clipboard converter (so nested lists survive) but apply it through
    // setContents: dangerouslyPasteHTML also calls setSelection(0), which focuses
    // the editor and makes the browser scroll to the last mounted field.
    const setQuillHtml = (html) => {
        quill.setContents(quill.clipboard.convert(html || ""), "silent");
    };

    useEffect(() => {
        if (quill && !loaded) {
            setQuillHtml(value);
            lastValueRef.current = value || "";

            const textChangeHandler = () => {
                const html = quill.container.firstChild.innerHTML;
                lastValueRef.current = html;
                onChange && onChange(html);
            };

            quill.on("text-change", textChangeHandler);
            setLoaded(true);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quill, loaded]);

    useEffect(() => {
        if (quill && loaded && value !== lastValueRef.current) {
            setQuillHtml(value);
            lastValueRef.current = value || "";
        }
    }, [quill, loaded, value]);

    return (
        <div className="textInput" {...rest}>
            <div
                ref={quillRef}
                tabIndex="0"
                onBlur={(e) => {
                    const html = e.currentTarget.firstChild.innerHTML;
                    onBlur && onBlur(sanitizeOutput(html));
                }}
            />
            {children}
        </div>
    );
};

export default TextInput;
